/** First month we show on admin growth charts (platform launch). */
export const PLATFORM_STATS_START = { year: 2025, month: 10 }; // November 2025 (0-indexed month)

export type GrowthRange = 6 | 12 | 'all';
export type GrowthMode = 'new' | 'cumulative';

export type MonthlyGrowthPoint = {
	key: string;
	label: string;
	shortLabel: string;
	newCount: number;
	cumulativeCount: number;
};

function monthKey(year: number, month: number): string {
	return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function parseDates(dates: (string | null | undefined)[]): Date[] {
	return dates
		.map((d) => (d ? new Date(d) : null))
		.filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()));
}

/** Every calendar month from Nov 2025 through the current month. */
export function enumeratePlatformMonths(): { year: number; month: number; key: string }[] {
	const now = new Date();
	const endYear = now.getFullYear();
	const endMonth = now.getMonth();
	const months: { year: number; month: number; key: string }[] = [];
	let y = PLATFORM_STATS_START.year;
	let m = PLATFORM_STATS_START.month;

	while (y < endYear || (y === endYear && m <= endMonth)) {
		months.push({ year: y, month: m, key: monthKey(y, m) });
		m += 1;
		if (m > 11) {
			m = 0;
			y += 1;
		}
	}

	return months;
}

function sliceMonthsForRange(
	all: { year: number; month: number; key: string }[],
	range: GrowthRange
): { year: number; month: number; key: string }[] {
	if (range === 'all') return all;
	return all.slice(-range);
}

function formatMonthLabel(year: number, month: number, style: 'long' | 'short'): string {
	const d = new Date(year, month, 1);
	if (style === 'long') {
		return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
	}
	return d.toLocaleDateString('en-GB', { month: 'short' });
}

function isInCalendarMonth(d: Date, year: number, month: number): boolean {
	return d.getFullYear() === year && d.getMonth() === month;
}

/**
 * Per-month new counts and cumulative totals from PLATFORM_STATS_START through today.
 */
export function buildMonthlyGrowthSeries(
	dates: (string | null | undefined)[],
	range: GrowthRange = 'all'
): MonthlyGrowthPoint[] {
	const parsed = parseDates(dates);
	const monthSlots = sliceMonthsForRange(enumeratePlatformMonths(), range);

	return monthSlots.map(({ year, month, key }) => {
		const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
		const newCount = parsed.filter((d) => isInCalendarMonth(d, year, month)).length;
		const cumulativeCount = parsed.filter((d) => d <= monthEnd).length;
		return {
			key,
			label: formatMonthLabel(year, month, 'long'),
			shortLabel: formatMonthLabel(year, month, 'short'),
			newCount,
			cumulativeCount
		};
	});
}
